import { forwardRef } from 'react';
import type { BaseInputFieldProps } from '../base-input-field/base-input-field';
import { BaseInputField } from '../base-input-field/base-input-field';
import type { BaseInputWrapperProps } from '../base-input-wrapper/base-input-wrapper';
import { BaseInputWrapper } from '../base-input-wrapper/base-input-wrapper';
import SearchIcon from '@tradera/blueprint/theme/icons2/search.svg?jsx';
import CloseIcon from '@tradera/blueprint/theme/icons2/close.svg?jsx';
import { Button } from 'tradera/components/atoms/button/button';
import { useControllableState } from 'tradera/components/form-controls/use-controllable-state';

export type SearchInputProps = {
    /**
     * If `onClear` is provided, `allowClear` will default to true.
     */
    allowClear?: boolean;
    /**
     * Called when user clicks the clear button.
     */
    onClear?: () => void;
} & Omit<BaseInputWrapperProps, 'children'> &
    Omit<BaseInputFieldProps, 'isInvalid' | 'type'>;

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
    function SearchInput(
        {
            value,
            defaultValue,
            label,
            hint,
            error,
            help,
            alt,
            collapsed = true,
            autoComplete = 'on',
            onChange,
            onClear,
            name,
            allowClear: allowClearProp,
            className,
            style,
            ...props
        },
        ref,
    ) {
        const allowClear = allowClearProp ?? typeof onClear === 'function';
        const [localValue, setLocalValue] = useControllableState<typeof value>({
            prop: value,
            defaultProp: defaultValue,
        });

        const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            /**
             * Set local value to be able to clear and react to changes if the component is not controlled.
             */
            setLocalValue(e.target.value);

            onChange?.(e);
        };

        const handleClear = () => {
            handleOnChange({
                target: { value: '' },
            } as React.ChangeEvent<HTMLInputElement>);
            onClear?.();
        };

        const button =
            allowClear &&
            (value?.toString() || localValue?.toString() || '').length > 0 ? (
                <Button
                    type="button"
                    // reset paddings:
                    // x-wise it's because therwise the field grows higher than other inputs.
                    // y-wise it's to align it with a suffix icon when it's not a button
                    theme="unbutton"
                    alt={alt ?? 'Clear'}
                    onClick={handleClear}>
                    <CloseIcon className="icon" />
                </Button>
            ) : (
                <SearchIcon className="icon" />
            );

        return (
            <BaseInputWrapper
                label={label}
                hint={hint}
                error={error}
                help={help}
                name={name}
                // so that we can add mt-1 and such to the wrapper
                className={className}
                style={style}
                collapsed={collapsed}>
                <BaseInputField
                    isInvalid={!!error}
                    // type="search" might get unwanted UI props that we already add with our suffix CrossIcon. We set inputmode="search" to get the same UI hints, hopefully.
                    type="text"
                    inputMode="search"
                    value={localValue}
                    onChange={handleOnChange}
                    autoComplete={autoComplete}
                    ref={ref}
                    name={name}
                    suffix={button}
                    {...props}
                />
            </BaseInputWrapper>
        );
    },
);
